/**
 * Onboarding journey log queue.
 *
 * Collects log events in memory and sends them to the backend in batches,
 * instead of one API call per event. This is a plain JS module (not a React
 * hook/component) on purpose: a module is loaded once per browser tab, so
 * its variables act as a natural singleton that survives component
 * re-renders/remounts, the same way a real message queue would.
 *
 * Full reasoning and the 4 flush triggers are agreed in
 * public/ONBOARDING_LOGGING_PLAN.md (section 6). Summary:
 *   1. Every FLUSH_INTERVAL_MS — but only if something more than passive
 *      viewing (SCREEN_VIEW/SCREEN_EXIT) happened since the last flush. An
 *      idle screen does not produce a network call just because time passed.
 *   2. Queue reaches MAX_QUEUE_SIZE — flush immediately, regardless of activity.
 *   3. Network comes back online — flush whatever built up while offline, regardless of activity.
 *   4. Tab is hidden/closing — flush immediately via fetch+keepalive, regardless of activity
 *      (an abandoned screen's view history must not be lost).
 *
 * IMPORTANT: nothing in this module may throw into the calling screen, and
 * nothing here may block or delay the real onboarding flow. Every failure
 * path is swallowed, same fire-and-forget spirit as the existing HubSpot
 * tracking in src/onboarding/hooks/useHubSpot.js.
 */
import { FLUSH_REASON, LOG_EVENT_TYPE } from './eventTypes';
import generateId from './generateId';
import { sendOnboardingLogs } from '../../redux/onboarding/actionCreator';
import { getItem } from '../../../utility/localStorageControl';

const FLUSH_INTERVAL_MS = 20000; // 20s — agreed timer flush interval
const MAX_QUEUE_SIZE = 25;       // agreed queue-full threshold
const STORAGE_KEY = 'hg_onboarding_log_queue';

// "Passive" event types — the host is just looking at something, not doing
// anything. A queue holding only these should NOT trigger the 20s timer
// flush by itself; that would mean sending a network request purely because
// time passed while the host sat idle on a screen. These still get sent —
// just riding along with the next real action, or swept up by queue-full /
// reconnect / tab-close, so an abandoned screen is never silently lost.
const PASSIVE_EVENT_TYPES = new Set([LOG_EVENT_TYPE.SCREEN_VIEW, LOG_EVENT_TYPE.SCREEN_EXIT]);

// ---------------------------------------------------------------------------
// Module-level (singleton) state.
// ---------------------------------------------------------------------------
let queue = [];
let dispatchRef = null;
let sessionInfo = { sessionSlug: null, hostEmail: null };
let timerId = null;
let initialized = false;
// True once something other than a passive view/exit event has been queued
// since the last flush. Only the periodic timer trigger checks this.
let hasActivitySinceFlush = false;

// ---- localStorage mirror ---------------------------------------------------
// Every event added to the in-memory queue is also mirrored here. If the tab
// is closed while offline, there is no JS left running to flush later — this
// mirror is what lets the NEXT page load in this browser recover and send
// those events instead of losing them silently.
const persistQueue = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // localStorage can fail (quota, private mode, etc.) — must never break logging or the app.
  }
};

const clearPersistedQueue = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // same as above — safe to ignore
  }
};

// ---- sending a batch --------------------------------------------------------

/**
 * Normal send path: through the redux thunk (sendOnboardingLogs), which
 * uses DataService/axios exactly like every other onboarding API call.
 * Used for triggers 1–3 (timer, queue full, reconnect).
 */
const sendViaRedux = (events, flushReason) => {
  if (!dispatchRef || events.length === 0) return;
  const batchPayload = {
    log_type: 'ONBOARDING',
    session_id: sessionInfo.sessionSlug,
    hostEmail: sessionInfo.hostEmail,
    flushReason,
    events,
  };
  try {
    dispatchRef(sendOnboardingLogs(batchPayload));
  } catch {
    // dispatch should not throw, but guard anyway — logging must never break the app.
  }
};

/**
 * "Tab is hidden/closing" send path (trigger 4).
 * A normal axios/fetch call started right as the tab closes can be killed by
 * the browser before it finishes. fetch(..., { keepalive: true }) tells the
 * browser to let this specific request finish in the background even after
 * the page is gone. navigator.sendBeacon() was considered instead, but it
 * does not support the Authorization header this route needs, so
 * fetch+keepalive is used here (the alternative called out in the plan).
 */
const sendViaKeepaliveFetch = (events, flushReason) => {
  if (events.length === 0) return;
  const batchPayload = {
    log_type: 'ONBOARDING',
    session_id: sessionInfo.sessionSlug,
    hostEmail: sessionInfo.hostEmail,
    flushReason,
    events,
  };
  try {
    const apiEndpoint = process.env.REACT_APP_PYTHON_API_ENDPOINT || '';
    const apiSecurityToken = process.env.REACT_APP_PYTHON_API_SECURITY_TOKEN || '';
    const token = getItem('onboarding_access_token') || getItem('access_token');

    fetch(`${apiEndpoint}/api/logs/log-client-error`, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'x-api-key': apiSecurityToken,
      },
      body: JSON.stringify(batchPayload),
    }).catch(() => {
      // silent — same fire-and-forget rule as everywhere else in this module
    });
  } catch {
    // silent
  }
};

// ---- core flush logic --------------------------------------------------------

/**
 * Pulls everything currently queued, clears the in-memory queue and its
 * localStorage mirror, then hands the batch off to be sent.
 *
 * The queue is cleared BEFORE the network call resolves, not after. The plan
 * calls for "no retries" — a dropped log is cheaper than a stuck queue or a
 * duplicate send, so once a batch is handed off, this module considers it done.
 */
const flushQueue = (reason, useKeepalive = false) => {
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  clearPersistedQueue();
  hasActivitySinceFlush = false; // whatever was pending is now handed off — reset for the next cycle

  if (useKeepalive) {
    sendViaKeepaliveFetch(events, reason);
  } else {
    sendViaRedux(events, reason);
  }
};

/**
 * Trigger 1's actual handler: only flush on the timer if something more than
 * passive viewing has happened since the last flush. If the host has just
 * been sitting on a screen doing nothing, this tick is a no-op — no network
 * call at all — and the queued SCREEN_VIEW/SCREEN_EXIT events simply wait
 * for the next real action (or the queue-full/reconnect/tab-close triggers,
 * which always flush regardless of activity, so nothing is lost).
 */
const handleTimerTick = () => {
  if (!hasActivitySinceFlush) return;
  flushQueue(FLUSH_REASON.TIMER);
};

// ---- recovery on load ---------------------------------------------------------

/**
 * On init, check for events left behind in localStorage from a previous tab
 * session (e.g. the user closed the tab while offline, so nothing could be
 * flushed at the time). If found, send them first — tagged "recovered" —
 * before anything new is logged in this session.
 */
const recoverPersistedQueue = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const leftover = JSON.parse(raw);
    clearPersistedQueue();
    if (Array.isArray(leftover) && leftover.length > 0) {
      sendViaRedux(leftover, FLUSH_REASON.RECOVERED);
    }
  } catch {
    // corrupt localStorage entry — drop it rather than let it break future logging
    clearPersistedQueue();
  }
};

// ---- flush triggers -------------------------------------------------------------

const handleOnline = () => {
  // Trigger 3: network just came back — send whatever built up while offline.
  flushQueue(FLUSH_REASON.RECONNECT);
};

const handleVisibilityChange = () => {
  // Trigger 4: tab is being backgrounded or closed. Mobile browsers in
  // particular can freeze/kill a hidden tab without firing a "close" event
  // at all, so this fires on every hide, not only on an actual close.
  if (document.visibilityState === 'hidden') {
    flushQueue(FLUSH_REASON.TAB_HIDDEN, /* useKeepalive */ true);
  }
};

const handleBeforeUnload = () => {
  // Second safety net for the same "tab closing" case — not every browser
  // reliably fires visibilitychange before actually closing the tab.
  flushQueue(FLUSH_REASON.TAB_HIDDEN, /* useKeepalive */ true);
};

// ---- public API -------------------------------------------------------------

/**
 * initLogQueue
 * Call once, on app mount (from OnboardingContext), passing the redux
 * dispatch function. Sets up all 4 flush triggers and recovers any logs
 * left behind by a previous tab session.
 *
 * Safe to call more than once (e.g. provider remount) — the listener/timer
 * setup below only runs a single time per page load, dispatchRef is simply
 * refreshed on every call so it always points at a live dispatch function.
 */
export const initLogQueue = (dispatch) => {
  dispatchRef = dispatch;
  if (initialized) return;
  initialized = true;

  recoverPersistedQueue();

  // Trigger 1: periodic flush — but only if there's been real activity (see handleTimerTick).
  timerId = setInterval(handleTimerTick, FLUSH_INTERVAL_MS);

  window.addEventListener('online', handleOnline);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handleBeforeUnload);
};

/**
 * setLoggerContext
 * Call whenever the onboarding session identity becomes known or changes
 * (sessionSlug / hostEmail). These two fields are attached once per BATCH,
 * not once per event — see section 8 of the plan.
 */
export const setLoggerContext = ({ sessionSlug, hostEmail } = {}) => {
  sessionInfo = {
    sessionSlug: sessionSlug ?? sessionInfo.sessionSlug,
    hostEmail: hostEmail ?? sessionInfo.hostEmail,
  };
};

/**
 * enqueueLog
 * Adds one fully-formed log event to the in-memory queue (and its
 * localStorage mirror). Sending only ever happens on one of the 4 flush
 * triggers above — this function never sends anything by itself, except
 * for the queue-full case which flushes immediately after adding.
 */
export const enqueueLog = (eventFields) => {
  try {
    const event = {
      logId: generateId(),
      timestamp: new Date().toISOString(),
      ...eventFields,
    };
    queue.push(event);
    persistQueue();

    if (!PASSIVE_EVENT_TYPES.has(event.eventType)) {
      hasActivitySinceFlush = true;
    }

    // Trigger 2: queue is full, flush now instead of waiting for the timer.
    if (queue.length >= MAX_QUEUE_SIZE) {
      flushQueue(FLUSH_REASON.QUEUE_FULL);
    }
  } catch {
    // Logging must never throw into the calling screen's code.
  }
};

/**
 * teardownLogQueue
 * Not used during normal app usage — the queue is meant to live for the
 * whole tab lifetime. Exposed for tests only, to stop the periodic timer
 * and remove listeners between test cases.
 */
export const teardownLogQueue = () => {
  if (timerId) {
    clearInterval(timerId);
    timerId = null;
  }
  window.removeEventListener('online', handleOnline);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('beforeunload', handleBeforeUnload);
  initialized = false;
  hasActivitySinceFlush = false;
};
